import { BLOCK_COLLAPSE_TYPES, SECTION_COLLAPSE_TYPES } from "@corca/tiptap";
import { partition } from "es-toolkit";

import { sectionBoundary } from "./sectionsCollapse";

import type { Translator } from "../EditorTranslator";
import type { CollapseType } from "@corca/tiptap";
import type { Editor } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import type { Selection } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";

export function isBlockCollapsible(node: PMNode | null | undefined): boolean {
  return !!node && (BLOCK_COLLAPSE_TYPES as readonly string[]).includes(node.type.name);
}

export function isSectionCollapsible(node: PMNode | null | undefined): boolean {
  return !!node && (SECTION_COLLAPSE_TYPES as readonly string[]).includes(node.type.name);
}

export function isCollapsible(node: PMNode | null | undefined): boolean {
  return isBlockCollapsible(node) || isSectionCollapsible(node);
}

export function getCollapseType(node: PMNode | null | undefined): CollapseType | undefined {
  if (isSectionCollapsible(node)) {
    return "section";
  }

  if (isBlockCollapsible(node)) {
    return "block";
  }

  return undefined;
}

export function isCollapsed(node: PMNode | null | undefined): node is Maybe<PMNode> {
  return !!node && node.attrs.collapsed === true;
}

export function clearCollapsedFlag(editor: Editor, pos: number): void {
  if (editor.isDestroyed) {
    return;
  }

  const node = editor.state.doc.nodeAt(pos);
  if (node?.attrs.collapsed !== true) {
    return;
  }

  editor.view.dispatch(
    editor.state.tr
      .setNodeMarkup(pos, undefined, { ...node.attrs, collapsed: false })
      .setMeta("addToHistory", false),
  );
}

export function adjacentNodeInDirection(
  view: EditorView,
  direction: "up" | "down",
  matches: (node: PMNode) => boolean,
): number | null {
  const { $head } = view.state.selection;
  const { depth: startDepth } = $head;
  const shift = direction === "down" ? 1 : -1;

  for (let depth = startDepth; depth >= 1; depth--) {
    const parent = $head.node(depth - 1);
    const index = $head.index(depth - 1) + shift;

    if (index < 0 || index >= parent.childCount) {
      continue;
    }

    return matches(parent.child(index)) ? $head.posAtIndex(index, depth - 1) : null;
  }

  return null;
}

export type ToggleTarget = {
  node: PMNode;
  pos: number;
};

export function collectToggleTargets(selection: Selection, doc: PMNode): ToggleTarget[] {
  const nodes: ToggleTarget[] = [];

  let currentSection: ToggleTarget | undefined;

  selection.ranges.forEach(({ $from, $to }) => {
    doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
      if (currentSection) {
        const boundary = sectionBoundary(currentSection.node, node);

        if (boundary === "none") {
          return false;
        }

        if (boundary === "after") {
          currentSection = undefined;
          return false;
        }

        currentSection = undefined;
      }

      if (isSectionCollapsible(node)) {
        currentSection = { node, pos };
        nodes.push(currentSection);

        return false;
      }

      if (isBlockCollapsible(node)) {
        nodes.push({ node, pos });
      }

      return false;
    });
  });

  return partition(nodes, ({ node }) => !!node.attrs.collapsed).reduce((a, b) =>
    b.length >= a.length ? b : a,
  );
}

export function collapseLabelText(
  collapsed: boolean,
  nodeTypeName: string,
  t?: Translator,
): string {
  if (!t) {
    return `${collapsed ? "Expand" : "Collapse"} ${nodeTypeName}`;
  }

  const nodeKey = `collapsibleNode.${nodeTypeName}` as Parameters<Translator>[0];
  const node = t.has(nodeKey) ? t(nodeKey) : nodeTypeName;
  return t(collapsed ? "expand" : "collapse", { node });
}
